import { ChakraProvider } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom'
import { theme } from '@/config/theme'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { AuthStatusNotification } from '@/components/common/AuthStatusNotification'
import { PrivacyPreferencesProvider } from '@/contexts/PrivacyPreferencesContext'
import { Router } from '@/components/Router'
import { loadChainRegistry } from '@/services/chainRegistryService'
import { resetChainRegistry } from '@shared/chainRegistryClient'
import { createConsentAwareColorModeManager } from '@/utils/privacyPreferences'
import PrivacyPreferencesBar from '@/components/common/PrivacyPreferencesBar'
import { DeferredNavigationProvider } from '@/navigation/DeferredNavigationProvider'
import {
  useNetworkEnvironment,
  type NetworkEnvironment,
  getNetworkStorageKey,
} from '@/hooks/useNetworkEnvironment'

const colorModeManager = createConsentAwareColorModeManager(
  getNetworkStorageKey('admin_portal_color_mode')
)
const showReactQueryDevtools = import.meta.env.DEV

const queryKeyContainsEnv = (queryKey: QueryKey, env: NetworkEnvironment): boolean => {
  return queryKey.some((segment) => {
    if (segment === env) {
      return true
    }
    if (Array.isArray(segment)) {
      return queryKeyContainsEnv(segment, env)
    }
    return false
  })
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

const RegistryBootstrapper = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const hasBootstrappedRef = useRef(false)
  const lastEnvRef = useRef<NetworkEnvironment | null>(null)
  const { currentEnv } = useNetworkEnvironment()

  useEffect(() => {
    if (!isAuthenticated) {
      hasBootstrappedRef.current = false
      lastEnvRef.current = null
      resetChainRegistry()
      return
    }

    if (isLoading) {
      return
    }

    if (hasBootstrappedRef.current && lastEnvRef.current === currentEnv) {
      return
    }

    hasBootstrappedRef.current = true
    lastEnvRef.current = currentEnv
    resetChainRegistry()
    loadChainRegistry(currentEnv === 'testnet').catch(error => {
      console.error('Failed to bootstrap chain registry', error)
      hasBootstrappedRef.current = false
    })
  }, [isAuthenticated, isLoading, currentEnv])

  return null
}

const NetworkQueryRefresher = () => {
  const { currentEnv } = useNetworkEnvironment()
  const queryClient = useQueryClient()
  const lastEnvRef = useRef<NetworkEnvironment | null>(null)

  useEffect(() => {
    if (!lastEnvRef.current) {
      lastEnvRef.current = currentEnv
      return
    }

    if (lastEnvRef.current === currentEnv) {
      return
    }

    const previousEnv = lastEnvRef.current
    lastEnvRef.current = currentEnv
    void queryClient.cancelQueries()
    queryClient.removeQueries({
      predicate: (query) => (
        queryKeyContainsEnv(query.queryKey, previousEnv)
        || queryKeyContainsEnv(query.queryKey, currentEnv)
      ),
    })
    queryClient.invalidateQueries({ refetchType: 'active' })
  }, [currentEnv, queryClient])

  return null
}

function App() {
  return (
    <ChakraProvider theme={theme} colorModeManager={colorModeManager}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AuthStatusNotification />
            <PrivacyPreferencesProvider>
              <RegistryBootstrapper />
              <NetworkQueryRefresher />
              <DeferredNavigationProvider>
                <Router />
              </DeferredNavigationProvider>
              <PrivacyPreferencesBar />
            </PrivacyPreferencesProvider>
          </AuthProvider>
        </BrowserRouter>
        {showReactQueryDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </ChakraProvider>
  )
}

export default App
